import { SidebarMenu } from "./SidebarMenu";
import { SidebarContent } from "./SidebarContent";
import {
	GameClipIntervalRequest,
	GPGameTeamResponse,
	GamePageResponse,
	GamePageLazyResponse,
	HighlightHandlers,
	GameEventDataExportRequest,
	GameEventFilterUtilityProps,
	VideoSidebar,
	RatingPutRequest,
	GameEventCommentProps,
	GPGamePlayerResponse,
	PlayerGradingSubmitPayload,
	CoachReviewPromises,
} from "../../types";
import type { ReportEventErrorHandler } from "../../report-error";
import { useGameStore } from "../../store";
import { useEffect } from "react";
import { useEditStore } from "../../edit-store";
import { useAttachStore } from "../../attach-store";

interface SidebarProps {
	gameData: GamePageResponse;
	gameDataLazy?: GamePageLazyResponse;
	highlightsHandlers: HighlightHandlers;
	showCoachingTools: boolean;
	handleCreateClip: (intervals: GameClipIntervalRequest[], name: string, description?: string) => void;
	handleOpenEditTeamSheetModal: (team: GPGameTeamResponse) => void;
	handleGameEventDataExport: (gameEventDataExportRequest: GameEventDataExportRequest) => void;
	gameEventFilters?: GameEventFilterUtilityProps;
	handleTeamSheetDataExport: (type: string) => void;
	variant?: "normal" | "video";
	handleSetPlayerRating?: (id: number, rating: RatingPutRequest) => void;
	handleSubmitPlayerGrading?: (payload: PlayerGradingSubmitPayload) => void | Promise<void>;
	toastMessage: (type: string, message: string) => void;
	gameEventComment?: GameEventCommentProps;
	players?: GPGamePlayerResponse[];
	onReportError?: ReportEventErrorHandler;
	coachReviewPromises?: CoachReviewPromises;
	userId?: number;
	activeContextTeamId?: number;
}

export function Sidebar({
	gameData,
	gameDataLazy,
	showCoachingTools,
	handleOpenEditTeamSheetModal,
	highlightsHandlers,
	handleCreateClip,
	handleGameEventDataExport,
	gameEventFilters,
	handleTeamSheetDataExport,
	variant = "normal",
	handleSetPlayerRating,
	handleSubmitPlayerGrading,
	toastMessage,
	gameEventComment,
	players,
	onReportError,
	coachReviewPromises,
	userId,
	activeContextTeamId,
}: SidebarProps) {
	const { sidebar, setSidebar, setSidebarAndFilter } = useGameStore();
	const { target: attachTarget } = useAttachStore();

	const { setShowClipCreateModal, setShowReportErrorModal, setSelectedEvents } = useEditStore();
	useEffect(() => {
		if (attachTarget !== null) {
			return;
		}

		if (sidebar !== VideoSidebar.EventList && sidebar !== VideoSidebar.EventsWithComments) {
			setShowClipCreateModal(false);
			setShowReportErrorModal(false);
			setSelectedEvents([]);
		}
	}, [attachTarget, setSelectedEvents, setShowClipCreateModal, setShowReportErrorModal, sidebar]);

	return (
		<div id={variant === "video" ? "video-sidebar" : undefined} className={variant === "normal" ? "sidebar-container" : "videoPlayer-sidebar"}>
			<SidebarMenu
				isAnalysed={gameData.game.isAnalysisFinished}
				showEvents={gameData.permissions.showEvents}
				showCoachingTools={showCoachingTools}
				sidebar={sidebar}
				setSidebar={setSidebar}
				setSidebarAndFilter={setSidebarAndFilter}
				eventCommentsPermission={gameData.permissions.showEventComments}
			/>
			<SidebarContent
				handleCreateClip={handleCreateClip}
				gameData={gameData}
				gameDataLazy={gameDataLazy}
				highlightsHandlers={highlightsHandlers}
				handleOpenEditTeamSheetModal={handleOpenEditTeamSheetModal}
				handleGameEventDataExport={handleGameEventDataExport}
				gameEventFilters={gameEventFilters}
				handleTeamSheetDataExport={handleTeamSheetDataExport}
				sidebar={sidebar}
				setSidebar={setSidebar}
				handleSetPlayerRating={handleSetPlayerRating}
				handleSubmitPlayerGrading={handleSubmitPlayerGrading}
				toastMessage={toastMessage}
				gameEventComment={gameEventComment}
				players={players}
				onReportError={onReportError}
				coachReviewPromises={coachReviewPromises}
				userId={userId}
				activeContextTeamId={activeContextTeamId}
			/>
		</div>
	);
}
